import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import { NoteStatus } from '~/lib/constants/AddPatientNoteModal.constants';
import { Path } from '~/routes/constants';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import useAppointments from '~/lib/hooks/useAppointments';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { AppointmentViewDetail } from '~/store/services/providerDashboard';

const JOIN_WINDOW_MINUTES = 15;

/**
 * Join + note actions for today's-sessions cards. The provider viewing the
 * dashboard is the appointment host, so the Zoom-join window is purely
 * time-based: from {@link JOIN_WINDOW_MINUTES} before start until the end.
 * Re-evaluated on a one-minute tick so the button appears as sessions approach.
 */
export const useTodaysSessionActions = (enabled = true) => {
  const history = useHistory();
  const { getZoomUrl } = useAppointments();
  const { showErrorToast } = useErrorToast();
  const { openNoteDrawer } = useEditNoteDrawerContext();

  // Only tick while the today tab is showing, so other tabs don't re-render
  // the whole card every minute.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, [enabled]);

  const canJoin = (appointment: AppointmentViewDetail): boolean => {
    const start = DateTime.fromISO(appointment.appointmentTime);
    if (!start.isValid) return false;
    const end = start.plus({ minutes: appointment.durationMinutes });
    const now = DateTime.now();
    return (
      now.plus({ minutes: JOIN_WINDOW_MINUTES }).diff(start).milliseconds >=
        0 && now.diff(end).milliseconds <= 0
    );
  };

  const onJoin = (appointmentExternalId: string) => {
    getZoomUrl(
      appointmentExternalId,
      (url) => {
        // Only open validated http(s) meeting URLs returned by the backend.
        if (url && /^https?:\/\//i.test(url)) {
          window.open(url, '_blank', 'noopener');
        }
      },
      (error, statusCode) =>
        showErrorToast(
          GlobalErrorCode.GET_ZOOM_MEETING_DETAILS,
          statusCode,
          error,
        ),
    );
  };

  const onOpenNote = (
    appointment: AppointmentViewDetail,
    patientId: string,
  ) => {
    const appointmentDate = DateTime.fromISO(appointment.appointmentTime, {
      setZone: true,
    })
      .toISODate();

    const appointmentsPath =
      `${Path.MAUD_PATIENTS}/${patientId}/appointments?date=${appointmentDate}`;

    if (appointment.noteStatus === NoteStatus.SIGNED) {
      history.push(
        `${Path.MAUD_PATIENTS}/${patientId}/notes?noteId=${appointment.noteExternalId}`,
      );
      return;
    }

    const apptDetails = {
      appointmentExternalId: appointment.appointmentExternalId,
      appointmentType:
        appointment.appointmentType ?? appointment.appointmentTypeLabel,
      appointmentTypeLabel: appointment.appointmentTypeLabel,
      appointmentStartDateTime:
        DateTime.fromISO(appointment.appointmentTime, {
          setZone: true,
        }).toISO() ?? appointment.appointmentTime,
    } as AppointmentDetailResponse;

    openNoteDrawer(
      patientId,
      appointment.noteExternalId ?? undefined,
      apptDetails,
    );
    history.push(appointmentsPath);
  };

  return { canJoin, onJoin, onOpenNote };
};
